import { ButtonMain, Input } from '@quero-delivery/quero-components-web';
import { useState } from 'react';
import { Controller, useForm } from 'react-hook-form';
import type { SubmitHandler } from 'react-hook-form';
import { toast } from 'react-toastify';
import { useParams } from 'react-router-dom';
import { Modal } from '@querodelivery/uni-react';
import type { TUserType } from '@/Interfaces/Data';
import { requiredErrorMessage } from '@/infra/YupDecorator';
import Requests from '@/Services/Requests';
import { useAppSelector } from '@/_store/hooks';
import PurpleButton from '@/Components/shared/purple-button/PurpleButton';
import { moneyInputFormat, moneyInputFormatToFloat } from '@/Utils/FnUtils';
import styles from './modal-launch.module.scss';
import { ENUM_LAUNCH, type Launch } from '../launch-bank/types';
import { selectUser } from '@/_store/modules/auth/selectors';
import { queryClient } from '@/_config/lib/react-query/query-client';
import { GET_BALANCE_DELIVERYMAN } from '@/_features/motoboys/api/constants';
import {
  GET_BALANCE_PLACE,
  GET_EXTRACTS_PLACES,
} from '@/_features/places/api/constants';
import { GET_EXTRACTS } from '@/_features/motoboys/extract/constants';
import { MODAL_WIDTH } from '@/_constants/styles';

export interface ModalLaunchProps {
  open: boolean;
  onClose: (open: boolean) => void;
  launchType: Launch;
  userType: TUserType;
}

interface IFormLaunch {
  value: string;
  description: string;
}

function ModalLaunch({
  open,
  onClose,
  launchType,
  userType,
}: ModalLaunchProps) {
  const manager = useAppSelector(selectUser);

  const [loadingLaunch, setLoadingLaunch] = useState(false);
  const { motoboyId, placeId } = useParams<{
    motoboyId: string;
    placeId: string;
  }>();

  const {
    handleSubmit,
    reset,
    control,
    formState: { errors },
  } = useForm<IFormLaunch>();

  const releaseId = motoboyId || placeId;

  if (!releaseId) {
    return null;
  }

  const handleSubmitLaunch: SubmitHandler<IFormLaunch> = async ({
    description,
    value,
  }) => {
    const valueToCents =
      launchType === 'CREDITO'
        ? moneyInputFormatToFloat(value) * 100
        : -(moneyInputFormatToFloat(value) * 100);

    setLoadingLaunch(true);

    try {
      let request;

      switch (userType) {
        case 'MOTOBOY':
          request = Requests.bank.launchCreditDebitMotoboy;
          break;
        case 'PLACE':
          request = Requests.bank.launchCreditDebitPlace;
          break;
        default:
          break;
      }

      if (!request) return null;

      // eslint-disable-next-line @typescript-eslint/no-explicit-any
      const response: any = await request(
        releaseId,
        description,
        Number(valueToCents.toFixed(0)),
        manager!.nome,
      );

      if (response.r) {
        toast.success(
          `${
            response.data.tipo === 'CREDITO'
              ? 'Crédito lançado'
              : 'Débito lançado'
          }`,
        );

        if (userType === 'MOTOBOY') {
          queryClient.invalidateQueries([
            GET_BALANCE_DELIVERYMAN,
            { deliverymanId: releaseId },
          ]);
          queryClient.invalidateQueries([GET_EXTRACTS]);
        } else {
          queryClient.invalidateQueries([
            GET_BALANCE_PLACE,
            { placeId: releaseId },
          ]);
          queryClient.invalidateQueries([GET_EXTRACTS_PLACES]);
        }
        reset();
        onClose(false);
      } else {
        toast.warn(response.errors.join('-'));
      }
      // eslint-disable-next-line @typescript-eslint/no-explicit-any
    } catch (error: any) {
      toast.error(error.response?.data.error);
    } finally {
      setLoadingLaunch(false);
    }
  };

  function validateValue(value: string) {
    if (moneyInputFormatToFloat(value) === 0) return 'Valor inválido';

    return undefined;
  }

  return (
    <Modal
      isOpen={open}
      onClose={() => onClose(!open)}
      width={MODAL_WIDTH.md}
      header={`Bank - Lançar ${
        launchType === ENUM_LAUNCH.CREDITO ? 'crédito' : 'débito'
      }`}
    >
      <div className={styles.container}>
        <form onSubmit={handleSubmit(handleSubmitLaunch)} id="form-launch">
          <div className={styles.inputContainer}>
            <Controller
              name="value"
              control={control}
              rules={{
                required: requiredErrorMessage,
                validate: validateValue,
              }}
              render={({ field }) => (
                <Input
                  {...field}
                  data-testid="input-value"
                  label={launchType === 'DEBITO' ? 'Débito' : 'Crédito'}
                  errorMessage={errors.value?.message}
                  onChange={event => {
                    const newValue = event.target.value;

                    field.onChange(
                      newValue ? `R$ ${moneyInputFormat(newValue)}` : '',
                    );
                  }}
                />
              )}
            />
            <Controller
              name="description"
              control={control}
              rules={{
                required: requiredErrorMessage,
              }}
              render={({ field }) => (
                <Input
                  {...field}
                  data-testid="input-description"
                  label="Descrição"
                  className={styles.inputStyle}
                  errorMessage={errors.description?.message}
                />
              )}
            />
          </div>
          <div className={styles.containerButtons}>
            <ButtonMain
              secondary
              textFirst="Cancelar"
              onClick={e => {
                e.preventDefault();
                onClose(false);
              }}
              width="100%"
            />
            <PurpleButton
              textFirst="Lançar"
              disabled={loadingLaunch}
              loading={loadingLaunch}
              type="submit"
              form="form-launch"
              width="100%"
            />
          </div>
        </form>
      </div>
    </Modal>
  );
}

export { ModalLaunch };
